import { useState, useCallback, useEffect, useMemo } from "react";
import { CampLocation } from "@/types/campLocation";
import { Offer } from "@/types/offer";
import { useOfferManagement } from "./useOfferManagement";
import { useOffersByOrganization } from "@/hooks/useOffersByOrganization";
import { db } from "@/services/firebaseService";
import { useAuth } from "@/context/AuthContext";
import { ensureValidDate } from "@/utils/dateUtils";
import { toast } from "sonner";
import { bulkUpdateOffers, publishAllOffersForLocationServer } from "@/services/offer/offerBatchUpdateService";
import { getCampLocationById } from '@/services/campLocationService';
import { filterOffersForTableView } from "@/services/offer/offerDisplayUtils";
import { createOffer, updateOffer, deleteOffer } from "@/services/offerService";
import { extractLocationId } from "@/services/offer/offerCopyHelpers";
import { emitAnalyticsEvent } from "@/lib/analytics/emitEvent";
import { ANALYTICS_EVENTS } from "@/lib/analytics/eventNames";
import { reportSaveRejection, SAVE_NO_RESULT_REASON } from "@/lib/analytics/saveOutcome";
import {
  buildOrgCalendarSessionEvents,
  ORG_CALENDAR_OTHER_KEY,
} from "@/services/offer/orgCalendarSessionEvents";
import { getCurrentVisibleMonth } from "../utils/calendarUtils";

export interface UseSessionsTabProps {
  /** Location mode: single location (location Sessions tab). */
  locationId?: string;
  location?: CampLocation;
  /** Org mode: sessions across all org locations (organization Sessions tab). */
  organizationId?: string | null;
}

const dummyLocationForOrgMode = {
  id: "",
  name: "",
  visibility: "v_draft",
  variations: [],
} as unknown as CampLocation;

export const useSessionsTab = (props: UseSessionsTabProps) => {
  const { locationId, location, organizationId } = props;
  const isOrgMode = organizationId != null && organizationId !== "";

  const { currentUser, isAdmin } = useAuth();
  const userId = currentUser?.uid || "";

  // Org mode: load offers for all org locations
  const orgOffersResult = useOffersByOrganization(isOrgMode ? organizationId : null);

  // Location mode: single-location offer management (dummy when org mode so hook still runs)
  const offerManagement = useOfferManagement(
    isOrgMode ? "" : (locationId ?? ""),
    db,
    userId,
    isOrgMode ? dummyLocationForOrgMode : (location ?? dummyLocationForOrgMode)
  );

  const [viewMode, setViewMode] = useState<"calendar" | "table">(
    isOrgMode ? "table" : "calendar"
  );
  const [dialogKey, setDialogKey] = useState<number>(0);
  const [currentOffer, setCurrentOffer] = useState<Offer | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isCopyOperation, setIsCopyOperation] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const [calendarProgramFilter, setCalendarProgramFilter] = useState<string | undefined>(undefined);
  const [calendarDateFilter, setCalendarDateFilter] = useState<'future' | 'past' | 'all' | 'placeholder'>('all');
  const [localTableDateFilter, setLocalTableDateFilter] = useState<'future' | 'past' | 'all' | 'placeholder'>('future');
  const [orgCalendarBaseMonth, setOrgCalendarBaseMonth] = useState<Date>(() =>
    getCurrentVisibleMonth()
  );

  const tableDateFilter = isOrgMode ? orgOffersResult.tableDateFilter : localTableDateFilter;
  const setTableDateFilter = isOrgMode ? orgOffersResult.setTableDateFilter : setLocalTableDateFilter;

  // Unified data source: location mode vs org mode
  const offers = isOrgMode ? orgOffersResult.offers : offerManagement.offers;
  const isLoading = isOrgMode ? orgOffersResult.isLoading : offerManagement.isLoading;
  const availabilityStatusOptions = isOrgMode
    ? orgOffersResult.availabilityStatusOptions
    : offerManagement.availabilityStatusOptions;
  const signupStatusOptions = isOrgMode
    ? orgOffersResult.signupStatusOptions
    : offerManagement.signupStatusOptions;
  const locations = isOrgMode ? orgOffersResult.locations : [];

  const refreshOffers = isOrgMode
    ? orgOffersResult.refreshOffers
    : offerManagement.refreshOffers;

  const orgCalendarData = useMemo(() => {
    if (!isOrgMode) return null;
    return buildOrgCalendarSessionEvents(
      offers,
      locations,
      orgCalendarBaseMonth
    );
  }, [isOrgMode, offers, locations, orgCalendarBaseMonth]);

  const sessionEvents = useMemo(() => {
    if (isOrgMode) return orgCalendarData?.sessionEvents ?? [];
    return offerManagement.sessionEvents;
  }, [isOrgMode, orgCalendarData, offerManagement.sessionEvents]);

  const calendarLegendVariations = useMemo(() => {
    if (!isOrgMode || !orgCalendarData) return [];
    return orgCalendarData.legendVariations;
  }, [isOrgMode, orgCalendarData]);

  const orgTopVariationKeySet = useMemo(() => {
    if (!orgCalendarData) return new Set<string>();
    return new Set(orgCalendarData.topVariationKeys);
  }, [orgCalendarData]);

  const lastOfferByVariation = useMemo(() => {
    if (isOrgMode) {
      const map: Record<string, Offer> = {};
      offers.forEach((offer) => {
        if (offer.variationKey) {
          const existing = map[offer.variationKey];
          const offerStart = new Date(offer.startDate instanceof Date ? offer.startDate : (offer.startDate as any)?.toDate?.() ?? offer.startDate);
          if (!existing || new Date(existing.startDate instanceof Date ? existing.startDate : (existing.startDate as any)?.toDate?.() ?? existing.startDate) < offerStart) {
            map[offer.variationKey] = offer;
          }
        }
      });
      return map;
    }
    return offerManagement.lastOfferByVariation;
  }, [isOrgMode, offers, offerManagement.lastOfferByVariation]);

  const setOfferManagementSelectedDate = offerManagement.setSelectedDate;

  // Debug data - log offers and status options
  useEffect(() => {

    // Detailed logging of first offer if available
    if (offers.length > 0) {
      const firstOffer = offers[0];
    }
  }, [offers, sessionEvents, availabilityStatusOptions, signupStatusOptions]);

  // Handle dialog operations - FIXED VERSION
  const handleOpenDialog = useCallback((offer?: Offer, isCopy = false) => {
    
    // Reset all dialog state first
    setIsCopyOperation(false);
    setCurrentOffer(null);
    
    // Then set the new state
    setIsCopyOperation(isCopy);
    
    if (offer) {
      if (isCopy) {
        // For copy operation, don't append " (Copy)" to the session title
        // but don't include the id (to create a new record)
        const { id, createdAt, lastEditedAt, createdBy, lastEditedBy, ...offerDataToCopy } = offer;
        
        const copyOffer = {
          ...offerDataToCopy,
          displayTitle: offerDataToCopy.displayTitle || ''
        };
        
        setCurrentOffer(copyOffer as Offer);
      } else {
        setCurrentOffer(offer);
      }
    } else {
      setCurrentOffer(null);
    }

    // Open the dialog
    setIsDialogOpen(true);
    
    // Increment the dialog key to force re-render the dialog component
    setDialogKey(prevKey => prevKey + 1);
    
  }, []); // Remove isDialogOpen dependency to prevent issues
  
  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setIsCopyOperation(false);

    // Clean up state after dialog closes with a delay
    setTimeout(() => {
      setCurrentOffer(null);
      setSelectedDate(null);
      // Also clear the date in the offer management hook
      setOfferManagementSelectedDate(null);
    }, 100);
  }, [setOfferManagementSelectedDate]); // Remove currentOffer dependency to prevent stale closures
  
  const handleSaveOffer = useCallback(async (offerData: any) => {

    try {
      const safeOfferData = {
        ...offerData,
        startDate: ensureValidDate(offerData.startDate),
        endDate: ensureValidDate(offerData.endDate)
      };
      if (safeOfferData.priceOverride === undefined) {
        safeOfferData.priceOverride = null;
      }

      if (isOrgMode) {
        const locationIdForOffer = extractLocationId(currentOffer!);
        if (!locationIdForOffer) {
          toast.error("Cannot determine location for this session");
          return;
        }
        if (isCopyOperation || !currentOffer?.id) {
          const created = await reportSaveRejection(
            () => createOffer(db, locationIdForOffer, safeOfferData, userId),
            (reason) => emitAnalyticsEvent(ANALYTICS_EVENTS.OFFER_CREATE_FAILURE, { locationId: locationIdForOffer, reason }),
          );
          // createOffer's return type allows null — a falsy result means nothing was
          // actually written, so the toast must not claim success (KB-2622).
          if (created) {
            toast.success(isCopyOperation ? "Session copied successfully" : "Session created successfully");
            emitAnalyticsEvent(ANALYTICS_EVENTS.OFFER_CREATE_SUBMIT, { offerId: created.id });
          } else {
            emitAnalyticsEvent(ANALYTICS_EVENTS.OFFER_CREATE_FAILURE, {
              locationId: locationIdForOffer,
              reason: SAVE_NO_RESULT_REASON,
            });
            toast.error(
              isCopyOperation
                ? "Session was not copied — no session was returned. Please try again."
                : "Session was not created — no session was returned. Please try again."
            );
          }
        } else {
          await reportSaveRejection(
            () => updateOffer(db, currentOffer.id, safeOfferData, userId),
            (reason) => emitAnalyticsEvent(ANALYTICS_EVENTS.OFFER_EDIT_FAILURE, { offerId: currentOffer.id, reason }),
          );
          toast.success("Session updated successfully");
          emitAnalyticsEvent(ANALYTICS_EVENTS.OFFER_EDIT_SAVE, { offerId: currentOffer.id });
        }
      } else {
        if (isCopyOperation || !currentOffer?.id) {
          const created = await offerManagement.addOffer(safeOfferData);
          // addOffer returns null on two paths — its own missing-data guard, or a caught
          // write failure (see useOfferManagement.ts) — and both already show their own
          // toast.error with the actual reason. Don't also claim success on top of it,
          // and don't pile a second, less specific error on top of the one it already
          // showed (KB-2622).
          if (created) {
            toast.success(isCopyOperation ? "Session copied successfully" : "Session created successfully");
            emitAnalyticsEvent(ANALYTICS_EVENTS.OFFER_CREATE_SUBMIT, { offerId: created.id });
          } else {
            emitAnalyticsEvent(ANALYTICS_EVENTS.OFFER_CREATE_FAILURE, { reason: SAVE_NO_RESULT_REASON });
          }
        } else {
          // updateOfferItem catches its own write failure and toasts it, so its
          // result is the only signal of whether the write landed.
          const updated = await offerManagement.updateOfferItem(currentOffer!.id, safeOfferData);
          if (updated) {
            toast.success("Session updated successfully");
            emitAnalyticsEvent(ANALYTICS_EVENTS.OFFER_EDIT_SAVE, { offerId: currentOffer!.id });
          } else {
            emitAnalyticsEvent(ANALYTICS_EVENTS.OFFER_EDIT_FAILURE, {
              offerId: currentOffer!.id,
              reason: SAVE_NO_RESULT_REASON,
            });
          }
        }
      }

      handleCloseDialog();
      await refreshOffers();
    } catch (error) {
      console.error("Error saving offer:", error);
      toast.error(isCopyOperation ? "Failed to copy session" : "Failed to save session");
    }
  }, [isOrgMode, currentOffer, handleCloseDialog, isCopyOperation, refreshOffers, userId, offerManagement]);

  // Handle day click on calendar - Fixed implementation
  const handleDayClick = useCallback((date: Date) => {

    // Ensure the date is valid
    const safeDate = ensureValidDate(date);

    // Set the selected date for use in the dialog
    setSelectedDate(safeDate);
    // Also update the date in the offer management hook
    setOfferManagementSelectedDate(safeDate);

    // Open the dialog with the selected date
    handleOpenDialog();
  }, [handleOpenDialog, setOfferManagementSelectedDate]);

  // Handle edit session on calendar
  const handleEditSession = useCallback((sessionEvent: any) => {
    // Find the corresponding offer
    const offer = offers.find(o => o.id === sessionEvent.id);
    if (offer) {
      handleOpenDialog(offer);
    } else {
      console.error("Could not find offer for session:", sessionEvent.id);
    }
  }, [handleOpenDialog, offers]);

  // Add session button handler - Fixed implementation
  const handleAddSession = useCallback(() => {
    const now = new Date();

    // Set today's date when adding from button click
    setSelectedDate(now);
    // Also update the date in the offer management hook
    setOfferManagementSelectedDate(now);
    
    // Open the dialog
    handleOpenDialog();
  }, [handleOpenDialog, setOfferManagementSelectedDate]);
  
  const handleDeleteOffer = useCallback(async (offerId: string) => {
    if (!offerId) {
      toast.error("Cannot delete session: Missing ID");
      return;
    }
    try {
      if (isOrgMode) {
        await deleteOffer(db, offerId);
      } else {
        await offerManagement.deleteOfferItem(offerId);
      }
      toast.success("Session deleted successfully");
      if (isDialogOpen) handleCloseDialog();
      await refreshOffers();
    } catch (error) {
      console.error("Error deleting offer:", error);
      toast.error("Failed to delete session");
    }
  }, [isOrgMode, offerManagement.deleteOfferItem, refreshOffers, isDialogOpen, handleCloseDialog]);

  // Handle copy offer - simplified approach
  const handleCopyOffer = useCallback((offerId: string) => {
    
    try {
      // Find the original offer
      const originalOffer = offers.find(offer => offer.id === offerId);
      
      if (!originalOffer) {
        console.error(`[handleCopyOffer] Could not find offer with ID: ${offerId}`);
        toast.error("Session not found for copying");
        return;
      }
      
      // Open the dialog with the offer data for copying
      handleOpenDialog(originalOffer, true);
      
    } catch (error) {
      console.error(`[handleCopyOffer] Error preparing to copy offer ${offerId}:`, error);
      toast.error("Failed to prepare copying session");
    }
  }, [handleOpenDialog, offers]);

  // New function: Handle publishing to offers (recalculating all derived fields)
  const handlePublishToOffers = useCallback(async () => {
    if (!locationId || !userId) {
      toast.error("Missing required information");
      return;
    }

    setIsPublishing(true);

    try {
      // Call server-side publish to update all offers (reuses server core)
      const result = await publishAllOffersForLocationServer(locationId);
      
      if (result.success) {
        const skip = result.skippedInSyncCount > 0
          ? `, ${result.skippedInSyncCount} already in sync`
          : "";
        toast.success(`Successfully updated ${result.updatedCount} sessions${skip}`);
        // Always reload from Firestore so the table / Get Full JSON match post-publish state
        await refreshOffers();
      } else {
        console.error("[handlePublishToOffers] Failed to update sessions");
        toast.error("Failed to update sessions");
      }
    } catch (error) {
      console.error("[handlePublishToOffers] Error publishing to offers:", error);
      toast.error("Error updating sessions: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsPublishing(false);
    }
  }, [db, locationId, userId, refreshOffers]);

  // New function: Handle bulk update of offers
  const handleBulkUpdateOffers = useCallback(async (
    offerIds: string[],
    updateData: Partial<Offer>,
    suppressToasts?: boolean
  ): Promise<void> => {
    if (!offerIds.length) {
      console.error("No offers selected for bulk update");
      return;
    }

    
    try {
      // Show loading toast
      const loadingToast = toast.loading(`Updating ${offerIds.length} sessions...`);

      // Call the bulk update service
      const result = await bulkUpdateOffers(db, offerIds, updateData, userId);
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      if (result.success) {
        if (!suppressToasts) {
          toast.success(`Successfully updated ${result.updatedCount} sessions`);
        }
        
        // Refresh offers list to show updated data
        await refreshOffers();
      } else {
        console.error("Failed to bulk update offers");
        if (!suppressToasts) {
          toast.error("Failed to update sessions");
        }
      }
    } catch (error) {
      console.error("Error in bulk update:", error);
      if (!suppressToasts) {
        toast.error("Error updating sessions: " + (error instanceof Error ? error.message : "Unknown error"));
      }
    }
  }, [db, userId, refreshOffers]);

  // Filter sessions based on selected program and date for CALENDAR view only
  const filteredCalendarEvents = useMemo(() => {
    let filteredEvents = [...sessionEvents];

    if (calendarProgramFilter) {
      if (isOrgMode && calendarProgramFilter === ORG_CALENDAR_OTHER_KEY) {
        filteredEvents = filteredEvents.filter((event) => {
          const k = event.variationKey || event.ageGroupId || "";
          return !orgTopVariationKeySet.has(k);
        });
      } else {
        filteredEvents = filteredEvents.filter(
          (event) => event.ageGroupId === calendarProgramFilter
        );
      }
    }

    try {
      const placeholderIds = new Set(
        offers.filter((o) => o.isPlaceholder).map((o) => o.id)
      );
      const showPlaceholders =
        tableDateFilter === "placeholder" ||
        calendarDateFilter === "placeholder";
      if (!showPlaceholders) {
        filteredEvents = filteredEvents.filter(
          (event) => !placeholderIds.has(event.id)
        );
      } else {
        filteredEvents = filteredEvents.filter((event) =>
          placeholderIds.has(event.id)
        );
      }
    } catch {
      const placeholderIds = new Set(
        offers.filter((o) => o.isPlaceholder).map((o) => o.id)
      );
      filteredEvents = filteredEvents.filter(
        (event) => !placeholderIds.has(event.id)
      );
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (calendarDateFilter === "future") {
      filteredEvents = filteredEvents.filter(
        (event) => event.startDate >= today || event.endDate >= today
      );
    } else if (calendarDateFilter === "past") {
      filteredEvents = filteredEvents.filter((event) => event.endDate < today);
    }

    return filteredEvents;
  }, [
    calendarDateFilter,
    calendarProgramFilter,
    sessionEvents,
    tableDateFilter,
    offers,
    isOrgMode,
    orgTopVariationKeySet,
  ]);
  
  // Filter offers for TABLE view (shared helper) - memoize to avoid new array every render and table re-render loops
  const filteredTableOffersList = useMemo(
    () => filterOffersForTableView(offers, tableDateFilter),
    [offers, tableDateFilter]
  );

  const [publishedLocation, setPublishedLocation] = useState<CampLocation | null>(null);

  useEffect(() => {
    if (isOrgMode) return;
    const locId = locationId ?? "";
    const loc = location ?? dummyLocationForOrgMode;
    const fetchPublishedLocation = async () => {
      try {
        const publishedLoc = await getCampLocationById(db, locId);
        if (publishedLoc) setPublishedLocation(publishedLoc);
        else setPublishedLocation(null);
      } catch (error) {
        console.error("[useSessionsTab] Error fetching published location", error);
        setPublishedLocation(null);
      }
    };
    if (loc.visibility !== "v_published") {
      fetchPublishedLocation();
    } else {
      setPublishedLocation(loc);
    }
  }, [isOrgMode, locationId, location?.visibility]);

  const locationForDialog = useMemo(() => {
    if (!isOrgMode || !currentOffer || locations.length === 0) return null;
    const locId = extractLocationId(currentOffer);
    return locations.find((l) => l.id === locId) ?? null;
  }, [isOrgMode, currentOffer, locations]);

  const publishedLocationResolved = isOrgMode ? locationForDialog : publishedLocation;

  return {
    viewMode,
    setViewMode,
    dialogKey,
    isLoading,
    offers,
    sessionEvents,
    isDialogOpen,
    currentOffer,
    selectedDate,
    isCopyOperation,
    isAdmin,
    isPublishing,
    calendarProgramFilter,
    setCalendarProgramFilter,
    calendarDateFilter,
    setCalendarDateFilter,
    tableDateFilter,
    setTableDateFilter,
    lastOfferByVariation,
    availabilityStatusOptions,
    signupStatusOptions,
    filteredCalendarEvents,
    filteredTableOffers: filteredTableOffersList,
    handleOpenDialog,
    handleCloseDialog,
    handleSaveOffer,
    handleDayClick,
    handleEditSession,
    handleAddSession,
    handleDeleteOffer,
    handleCopyOffer,
    handlePublishToOffers,
    refreshOffers,
    publishedLocation: publishedLocationResolved,
    handleBulkUpdateOffers,
    isOrgMode,
    locations,
    calendarBaseMonth: isOrgMode ? orgCalendarBaseMonth : undefined,
    setCalendarBaseMonth: isOrgMode ? setOrgCalendarBaseMonth : undefined,
    calendarLegendVariations,
  };
};
